import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSocket } from 'sonata-platform-components';
import { showNotification } from 'sonata-react-components';
import { ProcessUpload } from 'sonata-static-storage/lib/schemas/processUpload/processUpload.schema';

import { getProcessResults, processEntityFile, updateProcessStatus } from '../MultiFlightMap/multiFlightMap.resources';
import { handleItemsCallback } from './POIImporter.helper';

import type {
  Column,
  MultiFlightEntityType,
  WSFileProcessErrorMessage,
  WSFileProcessMessage
} from 'sonata-static-storage/lib/common/common.types';
import type { Venue } from 'sonata-static-storage/lib/schemas/venue/venue.schema';

export type ProcessStatus = 'idle' | 'processing' | 'success' | 'partial' | 'error';

type State = {
  file: File | null;
  possibleWrongCountry?: boolean;
  processId: string | null;
  status: ProcessStatus;
  uploadProcess?: ProcessUpload['uploadProcess'];
  venuesAdded: Venue[];
  wrongCountryCount?: number;
};

export type FileData = {
  columnDelimiter?: string;
  country?: string;
  decimalDelimiter?: string;
  exceedsMaxRows?: boolean;
  fileName: string;
  keysToReview: Column[];
  lineDelimiter?: string;
  totalLines?: number;
};

export function usePOIImporterFile({
  COMPONENT_ID,
  entity,
  entityId,
  userId
}: {
  COMPONENT_ID: string;
  entity: MultiFlightEntityType;
  entityId: string;
  onRefreshList?: () => void;
  userId: string;
}) {
  const { formatMessage } = useIntl();
  const { offEvent, onEvent } = useSocket();

  const [{ file, possibleWrongCountry, processId, status, uploadProcess, venuesAdded, wrongCountryCount }, setState] =
    useState<State>({
      file: null,
      processId: null,
      status: 'idle',
      venuesAdded: []
    });

  // The backend can notify the same finished process more than once; each notification would fetch and persist
  // the results again.
  const handledProcessIds = useRef(new Set<string>());

  const getProcessResultsToken: Record<string, any> = {};
  const processEntityFileToken: Record<string, any> = {};
  const updateProcessStatusToken: Record<string, any> = {};

  useEffect(() => {
    return () => {
      getProcessResultsToken?.cancel?.();
      processEntityFileToken?.cancel?.();
      offEvent(COMPONENT_ID, 'poiImporterProcessSuccess');
      offEvent(COMPONENT_ID, 'poiImporterProcessError');
    };
  }, []);

  const handleProcessSuccessEvent = async (
    message: WSFileProcessMessage,
    processId: string,
    uploadProcess: ProcessUpload['uploadProcess']
  ) => {
    try {
      if (message.processId === processId) {
        if (handledProcessIds.current.has(processId)) {
          return;
        }
        handledProcessIds.current.add(processId);
        const { items, possibleWrongCountry, wrongCountryCount } = await getProcessResults(
          processId,
          getProcessResultsToken
        );
        const newVenues = await handleItemsCallback(
          items,
          processId,
          userId,
          getProcessResultsToken,
          processEntityFileToken
        );

        if (possibleWrongCountry) {
          setState(prevState => ({
            ...prevState,
            possibleWrongCountry,
            status: 'success',
            uploadProcess,
            venuesAdded: newVenues,
            wrongCountryCount
          }));
        } else if (uploadProcess === 'attributionPOIUpload') {
          showNotification(
            'success',
            formatMessage({
              defaultMessage:
                'File processed successfully. Please refer to the panel located at the bottom right to continue.',
              id: `poiImporter.notification.poiFinderReady`
            }) as string
          );
          setState(prevState => ({ ...prevState, status: 'success', uploadProcess, venuesAdded: newVenues }));
        } else {
          const processStatus = items.some(({ status: itemStatus }: { status: boolean }) => !itemStatus)
            ? 'partial'
            : 'success';
          let defaultMessage = `File items (${newVenues.length}) successfully added`;
          if (processStatus === 'partial') {
            defaultMessage = `File items (${newVenues.length}) added, but there were some items unable to be processed`;
          }
          showNotification(
            'success',
            formatMessage(
              {
                defaultMessage,
                id: `poiImporter.notification.${processStatus}`
              },
              {
                count: newVenues.length
              }
            ) as string
          );
          setState(prevState => ({ ...prevState, status: 'success', uploadProcess, venuesAdded: newVenues }));
        }
      }
    } catch (_err) {
      handledProcessIds.current.delete(processId);
      showNotification(
        'error',
        formatMessage({
          defaultMessage: "File items couldn't be processed, please try again",
          id: 'poiImporter.notification.error'
        }) as string
      );
      setState(prevState => ({ ...prevState, status: 'error' }));
    }
  };

  const handleProcessErrorEvent = (message: WSFileProcessErrorMessage, processId: string) => {
    console.error(`${processId} - Error processing file: ${message.errorMsg}`);
    showNotification(
      'error',
      formatMessage({
        defaultMessage: "File items couldn't be processed, please try again",
        id: 'poiImporter.notification.error'
      }) as string
    );
    setState(prevState => ({ ...prevState, status: 'error' }));
  };

  const showProcessingNotification = (processUpload: ProcessUpload['uploadProcess']) => {
    if (processUpload === 'attributionPOIUpload') {
      return;
    }
    showNotification(
      'info',
      formatMessage({
        defaultMessage:
          "We are going to process your file, we'll notify you once it's done. This may take a few minutes depending on the file size. When it's finished, you can find the uploaded venues in the list.",
        id: 'poiImporter.notification.info'
      }) as string,
      {
        container: 'top-center',
        dismiss: {
          duration: 5000
        }
      }
    );
  };

  const cancelUploadProcess = async (processUpload: ProcessUpload['uploadProcess']) => {
    if (processId) {
      await updateProcessStatus(processId, 'cancelled', updateProcessStatusToken);
      offEvent(COMPONENT_ID, `${processUpload}ProcessSuccess`);
      offEvent(COMPONENT_ID, `${processUpload}ProcessError`);
      setState(prevState => ({
        ...prevState,
        file: null,
        processId: null,
        status: 'idle'
      }));
    }
  };

  const processBatchUploadFile = async (
    fileData: FileData,
    processUpload: ProcessUpload['uploadProcess'],
    poiFinderEnabled: boolean
  ): Promise<boolean> => {
    try {
      const resultProcess = await processEntityFile(
        entity,
        entityId,
        'venues',
        fileData,
        processUpload,
        processId,
        poiFinderEnabled,
        processEntityFileToken
      );
      if (fileData.exceedsMaxRows) {
        return true;
      }
      setState(prevState => ({ ...prevState, status: 'processing' }));
      onEvent(COMPONENT_ID, `${processUpload}ProcessSuccess`, async (message: WSFileProcessMessage) => {
        await handleProcessSuccessEvent(message, resultProcess.processId!, processUpload);
      });
      onEvent(COMPONENT_ID, `${processUpload}ProcessError`, (message: WSFileProcessErrorMessage) => {
        handleProcessErrorEvent(message, resultProcess.processId!);
      });
      setState(prevState => ({ ...prevState, processId: resultProcess.processId! }));
      showProcessingNotification(processUpload);
      return true;
    } catch (_e) {
      setState(prevState => ({ ...prevState, status: 'error' }));
      return false;
    }
  };

  const setFile = (file: File | null, incremental?: boolean) => {
    setState({
      file,
      processId: incremental ? processId : null,
      status: 'idle',
      uploadProcess: undefined,
      venuesAdded: []
    });
  };

  return {
    cancelUploadProcess,
    file,
    possibleWrongCountry,
    processBatchUploadFile,
    processId,
    setFile,
    status,
    uploadProcess,
    venuesAdded,
    wrongCountryCount
  };
}
